import { LitElement, html, css } from 'lit';

export class UIPassword extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true },
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    toggleMask: { type: Boolean }, // Def: true, whether to show toggle eye icon
    meter: { type: Boolean }, // Def: true, whether to show strength meter
    label: { type: String, reflect: true },
    
    // Internal States
    isMasked: { type: Boolean, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .password-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .password-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 44px; /* Space for eye icon */
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .password-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .password-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .password-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .toggle-btn {
      position: absolute;
      right: var(--ui-padding-x);
      border: none;
      background: transparent;
      padding: 0;
      color: var(--ui-text-muted);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .toggle-btn:hover:not(:disabled) {
      color: var(--ui-text);
    }

    .toggle-btn:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    /* Strength Meter Layout */
    .meter-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-top: 4px;
      animation: fadeIn 200ms ease-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .meter-bars {
      display: flex;
      gap: 6px;
      width: 100%;
    }

    .meter-bar {
      flex: 1;
      height: 4px;
      border-radius: var(--ui-radius-full);
      background-color: var(--ui-border);
      transition: background-color var(--ui-transition-normal);
    }

    /* Color Segment definitions for strength */
    .meter-bar.score-1 {
      background-color: var(--ui-danger); /* Red */
    }
    .meter-bar.score-2 {
      background-color: var(--ui-warning); /* Orange */
    }
    .meter-bar.score-3 {
      background-color: #f59e0b; /* Yellow/Amber */
    }
    .meter-bar.score-4 {
      background-color: var(--ui-success); /* Green */
    }

    .meter-label {
      font-size: var(--ui-font-size-sm);
      color: var(--ui-text-muted);
      font-weight: 500;
    }

    .meter-label span {
      font-weight: 700;
    }

    .meter-label.score-1 span { color: var(--ui-danger); }
    .meter-label.score-2 span { color: var(--ui-warning); }
    .meter-label.score-3 span { color: #f59e0b; }
    .meter-label.score-4 span { color: var(--ui-success); }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = '••••••••';
    this.disabled = false;
    this.required = false;
    this.toggleMask = true;
    this.meter = true;
    this.isMasked = true;
    this.label = '';
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity(
        { valueMissing: true },
        'Veuillez saisir un mot de passe.',
        this.shadowRoot.querySelector('.password-input')
      );
    } else {
      this.internals.setValidity({});
    }
  }

  _handleInput(e) {
    const val = e.target.value;
    this.value = val;
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }

  _toggleVisibility() {
    if (this.disabled) return;
    this.isMasked = !this.isMasked;
  }

  _getStrengthScore() {
    const val = this.value || '';
    if (!val) return 0;
    
    let score = 0;
    
    // Rule 1: Length >= 6
    if (val.length >= 6) score++;
    
    // Rule 2: Mixed case (lower + upper)
    if (/[a-z]/.test(val) && /[A-Z]/.test(val)) score++;
    
    // Rule 3: Digits
    if (/[0-9]/.test(val)) score++;
    
    // Rule 4: Special characters
    if (/[^A-Za-z0-9]/.test(val)) score++;
    
    // Limit to minimum length check: score must be at least 1 if length is good, or caps at 1 if length < 6
    if (val.length < 6 && score > 1) {
      return 1; // Penalty for short passwords
    }
    
    return score;
  }

  _getStrengthLabel(score) {
    switch (score) {
      case 1: return 'Faible';
      case 2: return 'Moyen';
      case 3: return 'Fort';
      case 4: return 'Excellent';
      default: return 'Trop court';
    }
  }

  render() {
    const score = this._getStrengthScore();
    const strengthLabel = this._getStrengthLabel(score);

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="password-container">
        <div class="input-wrapper">
          <input
            type="${this.isMasked ? 'password' : 'text'}"
            class="password-input" part="input"
            .value="${this.value}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            @input="${this._handleInput}"
          />
          
          ${this.toggleMask ? html`
            <button 
              type="button" 
              class="toggle-btn"
              ?disabled="${this.disabled}"
              @click="${this._toggleVisibility}"
              title="${this.isMasked ? 'Afficher le mot de passe' : 'Masquer le mot de passe'}"
            >
              ${this.isMasked ? html`
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              ` : html`
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              `}
            </button>
          ` : ''}
        </div>

        ${this.meter && this.value ? html`
          <div class="meter-container">
            <div class="meter-bars">
              <div class="meter-bar ${score >= 1 ? `score-${score}` : ''}"></div>
              <div class="meter-bar ${score >= 2 ? `score-${score}` : ''}"></div>
              <div class="meter-bar ${score >= 3 ? `score-${score}` : ''}"></div>
              <div class="meter-bar ${score >= 4 ? `score-${score}` : ''}"></div>
            </div>
            <div class="meter-label ${score > 0 ? `score-${score}` : ''}">
              Force du mot de passe : <span>${strengthLabel}</span>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }
}

if (!customElements.get('ui-password')) {
  customElements.define('ui-password', UIPassword);
}
